/**
 * AgentSimple - Clean GPT-style chat interface
 * - Local state for messages (no complex context)
 * - Citations panel
 * - Conversation history in sidebar
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Paper,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  Switch,
  Tooltip
} from '@mui/material';
import {
  Send as SendIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Menu as MenuIcon,
  Article as ArticleIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Psychology as PsychologyIcon,
  AttachMoney as CostIcon,
  Route as RouteIcon,
  Search as SearchIcon,
  Lightbulb as LightbulbIcon,
  ViewList as ViewListIcon,
  AccountTree as AccountTreeIcon,
} from '@mui/icons-material';
import { v3QueryStream, querySarahStream, ReasoningStep, CostData, DebugLogEntry } from '../services/v3RagService';
import { resolveSourceUrl } from '../utils/selfHealingRouter';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import StreamingProgress, { PipelineStage } from '../components/agent/StreamingProgress';
import GroupedSourcePanel from '../components/agent/GroupedSourcePanel';
import DebugPanel from '../components/agent/DebugPanel';

interface Source {
  id: string;
  content: string;
  similarity: number;
  document_id?: string;
  block_type?: string;
  ref_ids?: number[];
  metadata?: any;
  num?: number;  // Citation number from backend (e.g., 1, 9 for [1], [9])
}

interface MessageReasoning {
  steps: ReasoningStep[];
  cost?: CostData;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: Source[];
  reasoning?: MessageReasoning;
  suggestions?: string[];
  timestamp: Date;
  processingTimeMs?: number;
  documentsUsed?: number;
}

interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  updatedAt: Date;
  sessionId?: string;  // Backend Sarah session ID for follow-up context
}

const STORAGE_KEY = 'agent_conversations_v2';

// Detect if text contains Hebrew (for RTL support)
const isHebrew = (text: string): boolean => {
  if (!text) return false;
  const hebrewChars = (text.match(/[\u0590-\u05FF]/g) || []).length;
  return hebrewChars > text.length * 0.1;
};

// DEPRECATED: Backend now handles renumbering via corrected_answer event
// Kept for potential fallback use
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const renumberCitations = (text: string, sources: Source[]): { text: string; sources: Source[] } => {
  if (!text || !sources || sources.length === 0) {
    return { text, sources };
  }

  // Find all unique citation numbers in order of appearance
  const citationPattern = /\[(\d+)\]/g;
  const usedNumbers: number[] = [];
  const seen = new Set<number>();

  let match;
  while ((match = citationPattern.exec(text)) !== null) {
    const num = parseInt(match[1]);
    if (!seen.has(num)) {
      usedNumbers.push(num);
      seen.add(num);
    }
  }

  if (usedNumbers.length === 0) {
    return { text, sources };
  }

  // Create mapping: old number -> new sequential number
  const oldToNew: Record<number, number> = {};
  usedNumbers.forEach((oldNum, idx) => {
    oldToNew[oldNum] = idx + 1;  // 1-indexed
  });

  // Replace citations in text
  const renumberedText = text.replace(/\[(\d+)\]/g, (match, numStr) => {
    const oldNum = parseInt(numStr);
    const newNum = oldToNew[oldNum];
    return newNum ? `[${newNum}]` : match;
  });

  // Update sources with new numbers and filter to only used sources
  const renumberedSources = sources
    .filter(s => s.num !== undefined && oldToNew[s.num] !== undefined)
    .map(s => ({
      ...s,
      num: oldToNew[s.num!]
    }))
    .sort((a, b) => (a.num || 0) - (b.num || 0));

  return { text: renumberedText, sources: renumberedSources };
};

// Component to render text with clickable citations
const CitationText: React.FC<{
  children: React.ReactNode;
  sources?: Source[];
  onCitationClick: (idx: number) => void;
}> = ({ children, sources, onCitationClick }) => {
  if (typeof children !== 'string') {
    return <>{children}</>;
  }

  // Split text by citation patterns [1], [2], etc.
  const parts = children.split(/(\[\d+\])/g);

  return (
    <>
      {parts.map((part, i) => {
        const match = part.match(/\[(\d+)\]/);
        if (match) {
          const citationNum = parseInt(match[1]);
          // Find source by citation number (num field)
          // After renumbering, citations are sequential [1], [2], [3]...
          const source = sources?.find(s => s.num === citationNum);

          if (source) {
            return (
              <span
                key={i}
                onClick={(e) => {
                  e.stopPropagation();
                  // DEBUG: Log source data for citation click debugging
                  console.log(`[CITATION CLICK] Citation [${citationNum}] clicked:`, {
                    num: source.num,
                    document_id: source.document_id,
                    ref_id: source.metadata?.ref_id,
                    source_block_refs: source.ref_ids || source.metadata?.all_refs || source.metadata?.source_block_refs,
                    block_type: source.metadata?.block_type,
                  });
                  const resolved = resolveSourceUrl(source);
                  if (resolved) {
                    console.log(`[CITATION CLICK] Opening URL: ${resolved.url}${resolved.fallback ? ' (fallback)' : ''}`);
                    window.open(resolved.url, '_blank');
                  } else {
                    onCitationClick(citationNum);
                  }
                }}
                style={{
                  color: '#1976D2',
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  backgroundColor: '#E3F2FD',
                  padding: '1px 4px',
                  borderRadius: '3px',
                  fontSize: '0.85em'
                }}
                title={source.metadata?.title ? `${source.metadata.title}${source.metadata.report_date ? ` - ${source.metadata.report_date}` : ''}` : `View source ${match[1]}`}
              >
                {part}
              </span>
            );
          }
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
};

// Step label mapping for display
const STEP_LABELS: Record<string, string> = {
  research_context: 'Research Context',
  documents_found: 'Document Routing',
  blocks_retrieved: 'Block Selection',
  evidence_analysis: 'Evidence Verification',
  themes_detected: 'Themes',
  gateway: 'Gateway',
  research_plan: 'Research Plan',
  parallel_analysis: 'Parallel Analysis',
  verification: 'Verification',
  // Agentic tool-specific labels
  search_documents: 'Search',
  read_document: 'Read',
  lookup_entity: 'Lookup',
  get_document_outline: 'Outline',
  get_analyst_coverage: 'Analyst',
  get_sector_overview: 'Sector',
  plan_research: 'Plan',
  query_database: 'SQL',
  write_answer: 'Writing',
  recall_memory: 'Memory',
  delegate_research: 'Delegate',
  thinking: 'Thinking',
  self_check: 'Self-Check',
};

// Step-specific colors and icons for reasoning pills
const STEP_STYLES: Record<string, { bg: string; color: string; icon: React.ReactNode }> = {
  gateway: { bg: '#E8EAF6', color: '#283593', icon: <RouteIcon sx={{ fontSize: 14 }} /> },
  research_context: { bg: '#FFF3E0', color: '#E65100', icon: <LightbulbIcon sx={{ fontSize: 14 }} /> },
  research_plan: { bg: '#F3E5F5', color: '#6A1B9A', icon: <AccountTreeIcon sx={{ fontSize: 14 }} /> },
  documents_found: { bg: '#E8F5E9', color: '#2E7D32', icon: <SearchIcon sx={{ fontSize: 14 }} /> },
  blocks_retrieved: { bg: '#E3F2FD', color: '#1565C0', icon: <ViewListIcon sx={{ fontSize: 14 }} /> },
  evidence_analysis: { bg: '#FFF8E1', color: '#F57F17', icon: <PsychologyIcon sx={{ fontSize: 14 }} /> },
  themes_detected: { bg: '#FCE4EC', color: '#C62828', icon: <LightbulbIcon sx={{ fontSize: 14 }} /> },
  parallel_analysis: { bg: '#E0F7FA', color: '#00695C', icon: <AccountTreeIcon sx={{ fontSize: 14 }} /> },
  // Agentic tool-specific styles
  search_documents: { bg: '#E8F5E9', color: '#2E7D32', icon: <SearchIcon sx={{ fontSize: 14 }} /> },
  read_document: { bg: '#E3F2FD', color: '#1565C0', icon: <ViewListIcon sx={{ fontSize: 14 }} /> },
  lookup_entity: { bg: '#F3E5F5', color: '#6A1B9A', icon: <SearchIcon sx={{ fontSize: 14 }} /> },
  get_document_outline: { bg: '#E3F2FD', color: '#1565C0', icon: <ViewListIcon sx={{ fontSize: 14 }} /> },
  get_analyst_coverage: { bg: '#E8EAF6', color: '#283593', icon: <RouteIcon sx={{ fontSize: 14 }} /> },
  get_sector_overview: { bg: '#E8EAF6', color: '#283593', icon: <RouteIcon sx={{ fontSize: 14 }} /> },
  plan_research: { bg: '#F3E5F5', color: '#6A1B9A', icon: <AccountTreeIcon sx={{ fontSize: 14 }} /> },
  query_database: { bg: '#FFF3E0', color: '#E65100', icon: <ViewListIcon sx={{ fontSize: 14 }} /> },
  write_answer: { bg: '#E8F5E9', color: '#2E7D32', icon: <LightbulbIcon sx={{ fontSize: 14 }} /> },
  recall_memory: { bg: '#FFF8E1', color: '#F57F17', icon: <PsychologyIcon sx={{ fontSize: 14 }} /> },
  delegate_research: { bg: '#E0F7FA', color: '#00695C', icon: <AccountTreeIcon sx={{ fontSize: 14 }} /> },
  thinking: { bg: '#FFF8E1', color: '#F57F17', icon: <PsychologyIcon sx={{ fontSize: 14 }} /> },
  self_check: { bg: '#FFF3E0', color: '#E65100', icon: <PsychologyIcon sx={{ fontSize: 14 }} /> },
};

const ReasoningPanel: React.FC<{ reasoning: MessageReasoning }> = ({ reasoning }) => (
  <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0', fontSize: '0.85rem' }}>
    {reasoning.steps.map((step, i) => (
      <Box key={i} sx={{ mb: i < reasoning.steps.length - 1 ? 1 : 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {STEP_LABELS[step.step] || step.step}
        </Typography>
        {step.documents ? (
          <Box component="table" sx={{ width: '100%', mt: 0.5, borderCollapse: 'collapse', '& td, & th': { px: 1, py: 0.4, textAlign: 'left', fontSize: '0.8rem', borderBottom: '1px solid #eee' }, '& th': { color: '#777', fontWeight: 600 } }}>
            <thead><tr><th>Document</th><th style={{ width: 70 }}>Score</th><th>Reason</th></tr></thead>
            <tbody>
              {step.documents.map((doc, j) => (
                <tr key={j}>
                  <td style={{ color: '#333' }}>{doc.title}</td>
                  <td style={{ fontFamily: 'monospace', color: doc.confidence >= 0.7 ? '#2e7d32' : doc.confidence >= 0.4 ? '#e65100' : '#c62828' }}>
                    {(doc.confidence * 100).toFixed(0)}%
                  </td>
                  <td style={{ color: '#666' }}>{doc.reason}</td>
                </tr>
              ))}
            </tbody>
          </Box>
        ) : step.summary ? (
          <Typography variant="body2" sx={{ color: '#333', mt: 0.25, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {step.summary}
          </Typography>
        ) : null}
      </Box>
    ))}
    {reasoning.cost && (
      <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Cost Breakdown
        </Typography>
        {reasoning.cost.llm_calls.map((call, i) => (
          <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
            <Typography variant="caption" sx={{ color: '#666' }}>
              {call.label} ({call.model})
            </Typography>
            <Typography variant="caption" sx={{ color: '#666', fontFamily: 'monospace' }}>
              {call.input_tokens.toLocaleString()} in + {call.output_tokens.toLocaleString()} out &middot; ${call.cost.toFixed(5)}
            </Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, pt: 0.5, borderTop: '1px dashed #ddd' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#333' }}>
            Total ({reasoning.cost.db_queries} DB queries)
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#333', fontFamily: 'monospace' }}>
            {reasoning.cost.total_input_tokens.toLocaleString()} in + {reasoning.cost.total_output_tokens.toLocaleString()} out &middot; ${reasoning.cost.total_cost.toFixed(5)}
          </Typography>
        </Box>
      </Box>
    )}
  </Box>
);

export default function AgentSimple() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streamStatus, setStreamStatus] = useState<string>('');  // For showing streaming progress
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [useSarah, setUseSarah] = useState(true);  // Use Sarah (Agentic) by default
  const [, setAgentProgress] = useState<string>('');  // Current agent being executed
  const [pipelineStage, setPipelineStage] = useState<PipelineStage>('idle');
  const [stageDetails, setStageDetails] = useState<Partial<Record<PipelineStage, string>>>({});
  const [progressCollapsed, setProgressCollapsed] = useState(false);
  const [expandedReasoning, setExpandedReasoning] = useState<Set<string>>(new Set());
  const [expandedPills, setExpandedPills] = useState<Map<string, Set<number>>>(new Map());
  const [debugLogs, setDebugLogs] = useState<DebugLogEntry[]>([]);
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugVisible, setDebugVisible] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Copy answer to clipboard
  const handleCopy = async (messageId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(messageId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Track window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load conversations from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setConversations(parsed.map((c: any) => ({
          ...c,
          updatedAt: new Date(c.updatedAt),
          messages: c.messages.map((m: any) => ({ ...m, timestamp: new Date(m.timestamp) }))
        })));
      }
    } catch (e) {
      console.error('Failed to load conversations:', e);
    }
  }, []);

  // Save conversations to localStorage
  useEffect(() => {
    if (conversations.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(conversations));
    }
  }, [conversations]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load messages when conversation changes
  useEffect(() => {
    if (currentId) {
      const conv = conversations.find(c => c.id === currentId);
      if (conv) {
        setMessages(conv.messages);
      }
    } else {
      setMessages([]);
    }
  }, [currentId, conversations]);

  const saveConversation = (msgs: Message[], backendSessionId?: string) => {
    if (msgs.length === 0) return;

    const title = msgs[0].content.slice(0, 40) + (msgs[0].content.length > 40 ? '...' : '');

    if (currentId) {
      // Update existing — preserve or update sessionId
      setConversations(prev => prev.map(c =>
        c.id === currentId
          ? { ...c, messages: msgs, title, updatedAt: new Date(), sessionId: backendSessionId || c.sessionId }
          : c
      ));
    } else {
      // Create new
      const newId = Date.now().toString();
      const newConv: Conversation = {
        id: newId,
        title,
        messages: msgs,
        updatedAt: new Date(),
        sessionId: backendSessionId
      };
      setConversations(prev => [newConv, ...prev]);
      setCurrentId(newId);
    }
  };

  const handleSend = async (overrideMessage?: string) => {
    const messageText = overrideMessage || input.trim();
    if (!messageText || loading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: messageText,
      timestamp: new Date()
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    setStreamStatus('');
    setAgentProgress('');
    setPipelineStage('understanding');
    setStageDetails({});
    setProgressCollapsed(false);
    setDebugLogs([]);

    // Create placeholder for assistant message
    const assistantId = (Date.now() + 1).toString();
    const assistantMessage: Message = {
      id: assistantId,
      role: 'assistant',
      content: '',
      sources: [],
      timestamp: new Date()
    };

    // Add empty assistant message that will be updated
    setMessages([...newMessages, assistantMessage]);

    // Detect query language for response language
    const queryLanguage = isHebrew(userMessage.content) ? 'Hebrew' : 'English';

    if (useSarah) {
      // Use Sarah - Agentic AI Assistant (streaming)
      const currentConv = currentId ? conversations.find(c => c.id === currentId) : null;
      const backendSessionId = currentConv?.sessionId;

      await querySarahStream(
        userMessage.content,
        {
          onStatus: (status) => {
            setAgentProgress(status);
            // Map status text to pipeline stage
            const lower = status.toLowerCase();
            if (lower.includes('understand') || lower.includes('classif') || lower.includes('intent')) {
              setPipelineStage('understanding');
            } else if (lower.includes('routing') || lower.includes('finding') || lower.includes('document')) {
              setPipelineStage('routing');
            } else if (lower.includes('retriev') || lower.includes('block') || lower.includes('read')) {
              setPipelineStage('retrieving');
            } else if (lower.includes('generat') || lower.includes('synthes') || lower.includes('writing')) {
              setPipelineStage('generating');
            }
          },
          onChunk: (chunk, fullText) => {
            // First chunk: collapse progress and set generating stage
            if (!progressCollapsed) {
              setProgressCollapsed(true);
              setPipelineStage('generating');
            }
            setMessages(prev => prev.map(m =>
              m.id === assistantId
                ? { ...m, content: fullText }
                : m
            ));
          },
          onCorrectedAnswer: (correctedText) => {
            console.log('[Sarah] Received corrected_answer, updating message');
            setMessages(prev => prev.map(m =>
              m.id === assistantId
                ? { ...m, content: correctedText }
                : m
            ));
          },
          onReasoning: (step) => {
            // Map reasoning step to pipeline stage + details
            const stepName = step.step?.toLowerCase() || '';
            if (stepName === 'gateway' || stepName === 'research_context' || stepName === 'research_plan') {
              setPipelineStage('understanding');
              setStageDetails(prev => ({ ...prev, understanding: step.summary || step.step }));
            } else if (stepName === 'documents_found') {
              setPipelineStage('routing');
              const docCount = step.documents?.length;
              setStageDetails(prev => ({ ...prev, routing: docCount ? `${docCount} docs found` : step.summary || '' }));
            } else if (stepName === 'blocks_retrieved' || stepName === 'evidence_analysis') {
              setPipelineStage('retrieving');
              setStageDetails(prev => ({ ...prev, retrieving: step.summary || step.step }));
            } else if (stepName === 'themes_detected') {
              setStageDetails(prev => ({ ...prev, retrieving: step.summary || 'Themes detected' }));
            }
            setMessages(prev => prev.map(m => {
              if (m.id !== assistantId) return m;
              const existing = m.reasoning || { steps: [] };
              return { ...m, reasoning: { ...existing, steps: [...existing.steps, step] } };
            }));
          },
          onDebugLog: (entry) => {
            setDebugLogs(prev => [...prev, entry]);
            setDebugVisible(true);
          },
          onSuggestions: (suggestions) => {
            setMessages(prev => prev.map(m =>
              m.id === assistantId
                ? { ...m, suggestions }
                : m
            ));
          },
          onSources: (sources) => {
            setMessages(prev => prev.map(m => {
              if (m.id !== assistantId) return m;

              const mappedSources: Source[] = sources.map((s: any, i: number) => ({
                id: s.id || `source-${i}`,
                content: s.content || s.metadata?.title || '',
                similarity: s.similarity || 0.9,
                document_id: s.document_id,
                block_type: s.block_type || 'text',
                metadata: {
                  title: s.metadata?.title,
                  block_id: s.metadata?.block_id,
                  ref_id: s.ref_id ?? s.metadata?.ref_id,
                  page: s.page ?? s.metadata?.page,
                  section_path: s.breadcrumb,
                  report_date: s.metadata?.report_date,
                  source_block_refs: s.ref_ids || s.metadata?.all_refs || (s.blocks_cited || []).map((b: any) => b.ref_id).filter(Boolean),
                },
                num: s.citation_num || s.num || (i + 1)
              }));

              return { ...m, sources: mappedSources };
            }));
          },
          onDone: (processingTimeMs, documentsUsed, sessionId, cost) => {
            console.log(`[Sarah] Query completed in ${processingTimeMs}ms using ${documentsUsed} documents, session: ${sessionId}`);
            setAgentProgress('');
            setStreamStatus('');
            setPipelineStage('idle');
            setStageDetails({});
            setProgressCollapsed(false);
            setLoading(false);
            // Attach cost data + stats to the message
            setMessages(prev => prev.map(m => {
              if (m.id !== assistantId) return m;
              const existing = m.reasoning || { steps: [] };
              return {
                ...m,
                reasoning: cost ? { ...existing, cost } : existing,
                processingTimeMs,
                documentsUsed,
              };
            }));
            setMessages(prev => {
              saveConversation(prev, sessionId);
              return prev;
            });
          },
          onError: (error) => {
            console.error('[Sarah] Stream error:', error);
            setAgentProgress('');
            setMessages(prev => prev.map(m => {
              if (m.id !== assistantId) return m;
              // If we already have partial content, keep it and append error note
              if (m.content && m.content.length > 20) {
                return { ...m, content: m.content + `\n\n---\n*${error}*` };
              }
              return { ...m, content: `Sorry, I encountered an error: ${error}. Please try again.` };
            }));
            setLoading(false);
          }
        },
        queryLanguage,
        backendSessionId
      );
    } else {
      // Use legacy streaming query
      await v3QueryStream(
        userMessage.content,
        {
          onStatus: (status) => {
            setStreamStatus(status);
          },
          onChunk: (chunk, fullText) => {
            setMessages(prev => prev.map(m =>
              m.id === assistantId
                ? { ...m, content: fullText }
                : m
            ));
          },
          onCorrectedAnswer: (correctedText) => {
            console.log('[AgentSimple] Received corrected_answer, updating message');
            setMessages(prev => prev.map(m =>
              m.id === assistantId
                ? { ...m, content: correctedText }
                : m
            ));
          },
          onSources: (sources) => {
            setMessages(prev => prev.map(m => {
              if (m.id !== assistantId) return m;

              const mappedSources: Source[] = sources.map((s: any, i: number) => ({
                id: s.id || `source-${i}`,
                content: s.content || '',
                similarity: s.similarity || 0,
                document_id: s.document_id,
                block_type: s.block_type,
                metadata: s.metadata,
                num: s.num
              }));

              return { ...m, sources: mappedSources };
            }));
          },
          onDone: (processingTimeMs, documentsUsed) => {
            console.log(`Query completed in ${processingTimeMs}ms using ${documentsUsed} documents`);
            setStreamStatus('');
            setLoading(false);
            setMessages(prev => {
              saveConversation(prev);
              return prev;
            });
          },
          onError: (error) => {
            console.error('Stream error:', error);
            setMessages(prev => prev.map(m => {
              if (m.id !== assistantId) return m;
              if (m.content && m.content.length > 20) {
                return { ...m, content: m.content + `\n\n---\n*${error}*` };
              }
              return { ...m, content: `Sorry, I encountered an error: ${error}. Please try again.` };
            }));
            setStreamStatus('');
            setLoading(false);
          }
        },
        queryLanguage
      );
    }
  };

  const startNewConversation = () => {
    setCurrentId(null);
    setMessages([]);
    setSelectedMessage(null);
    setSidebarOpen(false);
  };

  const selectConversation = (id: string) => {
    setCurrentId(id);
    setSelectedMessage(null);
    setSidebarOpen(false);
  };

  const deleteConversation = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setConversations(prev => prev.filter(c => c.id !== id));
    if (currentId === id) {
      setCurrentId(null);
      setMessages([]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Sidebar content
  const SidebarContent = () => (
    <Box sx={{ width: 280, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#1A2341' }}>
      <Box sx={{ p: 2 }}>
        <IconButton 
          onClick={startNewConversation}
          sx={{ 
            width: '100%', 
            bgcolor: '#4A90E2', 
            color: 'white',
            borderRadius: 2,
            '&:hover': { bgcolor: '#357ABD' }
          }}
        >
          <AddIcon sx={{ mr: 1 }} />
          <Typography>New Chat</Typography>
        </IconButton>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <List sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        {conversations.map(conv => (
          <ListItemButton
            key={conv.id}
            selected={currentId === conv.id}
            onClick={() => selectConversation(conv.id)}
            sx={{
              mx: 1,
              borderRadius: 1,
              mb: 0.5,
              '&.Mui-selected': { bgcolor: 'rgba(74,144,226,0.2)' },
              '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' }
            }}
          >
            <ListItemText 
              primary={conv.title}
              primaryTypographyProps={{ 
                noWrap: true, 
                sx: { color: 'white', fontSize: 14 } 
              }}
              secondary={conv.messages.length + ' messages'}
              secondaryTypographyProps={{ 
                sx: { color: 'rgba(255,255,255,0.5)', fontSize: 12 } 
              }}
            />
            <IconButton 
              size="small" 
              onClick={(e) => deleteConversation(conv.id, e)}
              sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#f44336' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </ListItemButton>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: '#F7F9FB' }}>
      {/* Desktop Sidebar */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <SidebarContent />
      </Box>

      {/* Mobile Drawer */}
      <Drawer open={sidebarOpen} onClose={() => setSidebarOpen(false)}>
        <SidebarContent />
      </Drawer>

      {/* Main Chat Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Header */}
        <Box sx={{
          p: 2,
          borderBottom: '1px solid #E0E0E0',
          bgcolor: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <IconButton
            onClick={() => setSidebarOpen(true)}
            sx={{ display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A2341', flex: 1 }}>
            Research Assistant
          </Typography>

          {/* Sarah (Agentic) Toggle */}
          <Tooltip title={useSarah ? "Sarah: Multi-agent pipeline with comparison tables" : "Classic: Streaming response"}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PsychologyIcon sx={{ color: useSarah ? '#4A90E2' : '#999', fontSize: 20 }} />
              <Typography variant="caption" sx={{ color: useSarah ? '#4A90E2' : '#999', fontWeight: 600 }}>
                Sarah
              </Typography>
              <Switch
                checked={useSarah}
                onChange={(e) => setUseSarah(e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#4A90E2',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#4A90E2',
                  },
                }}
              />
            </Box>
          </Tooltip>
        </Box>

        {/* Messages */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {messages.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              color: '#666'
            }}>
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                Ask me anything about your research
              </Typography>
              <Typography variant="body2" color="text.secondary">
                I can search across all documents and provide cited answers
              </Typography>
            </Box>
          ) : (
            messages.map((msg, idx) => {
              const msgIsHebrew = isHebrew(msg.content);
              return (
              <Box
                key={msg.id}
                sx={{
                  mb: { xs: 1.5, sm: 2.5 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  width: '100%'
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    maxWidth: msg.role === 'user' ? { xs: '85%', sm: '75%' } : { xs: '95%', sm: '85%' },
                    bgcolor: msg.role === 'user' ? '#4A90E2' : 'white',
                    color: msg.role === 'user' ? 'white' : '#333',
                    borderRadius: 2,
                    border: msg.role === 'assistant' ? '1px solid #E0E0E0' : 'none',
                    direction: msgIsHebrew ? 'rtl' : 'ltr',
                    position: 'relative'
                  }}
                >
                  {/* Copy button for assistant messages */}
                  {msg.role === 'assistant' && (
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(msg.id, msg.content)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(0,0,0,0.04)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' }
                      }}
                    >
                      {copiedId === msg.id ? (
                        <CheckIcon sx={{ fontSize: 16, color: '#4CAF50' }} />
                      ) : (
                        <CopyIcon sx={{ fontSize: 16, color: '#666' }} />
                      )}
                    </IconButton>
                  )}

                  {msg.role === 'user' ? (
                    <Typography sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: { xs: '0.85rem', sm: '0.925rem' } }}>
                      {msg.content}
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        '& h1, & h2': {
                          fontSize: { xs: '0.95rem', sm: '1.1rem' },
                          fontWeight: 700,
                          color: '#1a2341',
                          mt: 1.5,
                          mb: 0.75,
                          borderBottom: '1px solid #e0e0e0',
                          pb: 0.5
                        },
                        '& h3': {
                          fontSize: { xs: '0.88rem', sm: '1rem' },
                          fontWeight: 600,
                          color: '#1a2341',
                          mt: 1.25,
                          mb: 0.5
                        },
                        '& p': {
                          mb: 0.75,
                          lineHeight: 1.55,
                          fontSize: { xs: '0.85rem', sm: '0.925rem' },
                          color: '#2c3e50'
                        },
                        '& ul, & ol': {
                          pl: 2,
                          mb: 0.75
                        },
                        '& li': {
                          mb: 0.4,
                          lineHeight: 1.5,
                          fontSize: { xs: '0.85rem', sm: '0.925rem' },
                          color: '#2c3e50'
                        },
                        '& strong': {
                          fontWeight: 700,
                          color: '#1a2341'
                        },
                        '& hr': {
                          my: 1,
                          border: 'none',
                          borderTop: '1px solid #e0e0e0'
                        },
                        '& table': {
                          display: 'block',
                          overflowX: 'auto',
                          WebkitOverflowScrolling: 'touch',
                          width: '100%',
                          borderCollapse: 'collapse',
                          mb: 1.5,
                          mt: 1,
                          fontSize: { xs: '0.75rem', sm: '0.82rem' },
                          whiteSpace: 'nowrap',
                          border: '1px solid #d0d7de',
                          borderRadius: '6px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                        },
                        '& th, & td': {
                          border: '1px solid #d0d7de',
                          px: { xs: 0.75, sm: 1.25 },
                          py: { xs: 0.5, sm: 0.7 },
                          whiteSpace: 'normal',
                          minWidth: 50,
                          lineHeight: 1.5,
                        },
                        '& th': {
                          bgcolor: '#f0f3f6',
                          fontWeight: 700,
                          color: '#1a2341',
                          textAlign: 'center',
                          borderBottom: '2px solid #c0c8d0',
                          fontSize: { xs: '0.73rem', sm: '0.8rem' },
                          letterSpacing: '0.01em',
                        },
                        '& td': {
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                        },
                        '& td:first-of-type': {
                          textAlign: 'left',
                          fontWeight: 600,
                          color: '#1a2341',
                        },
                        '& tr:nth-of-type(even)': {
                          bgcolor: '#f8f9fb',
                        },
                        '& tr:hover': {
                          bgcolor: '#eef2f7',
                        },
                        '& tr:last-child td': {
                          borderBottom: 'none',
                        },
                        '& code': {
                          bgcolor: '#f5f5f5',
                          px: 0.5,
                          py: 0.25,
                          borderRadius: 0.5,
                          fontSize: '0.85rem',
                          fontFamily: 'monospace'
                        },
                        '& a': {
                          color: '#1976D2',
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }
                      }}
                    >
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          // Custom text renderer for paragraphs with citations
                          p: ({ children }) => (
                            <p>
                              {React.Children.map(children, child => {
                                if (typeof child === 'string') {
                                  return (
                                    <CitationText
                                      sources={msg.sources}
                                      onCitationClick={(idx) => setSelectedMessage(msg)}
                                    >
                                      {child}
                                    </CitationText>
                                  );
                                }
                                return child;
                              })}
                            </p>
                          ),
                          // Custom text renderer for list items with citations
                          li: ({ children }) => (
                            <li>
                              {React.Children.map(children, child => {
                                if (typeof child === 'string') {
                                  return (
                                    <CitationText
                                      sources={msg.sources}
                                      onCitationClick={(idx) => setSelectedMessage(msg)}
                                    >
                                      {child}
                                    </CitationText>
                                  );
                                }
                                return child;
                              })}
                            </li>
                          ),
                          // Custom renderer for table cells with citations
                          td: ({ children }) => (
                            <td>
                              {React.Children.map(children, child => {
                                if (typeof child === 'string') {
                                  return (
                                    <CitationText
                                      sources={msg.sources}
                                      onCitationClick={(idx) => setSelectedMessage(msg)}
                                    >
                                      {child}
                                    </CitationText>
                                  );
                                }
                                return child;
                              })}
                            </td>
                          ),
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    </Box>
                  )}
                </Paper>

                {/* Sources + Reasoning pills for assistant messages */}
                {msg.role === 'assistant' && (msg.sources?.length || msg.reasoning?.steps?.length) ? (
                  <Box sx={{ mt: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                    {msg.sources && msg.sources.length > 0 && (
                      <Chip
                        icon={<ArticleIcon />}
                        label={`${msg.sources.length} sources`}
                        size="small"
                        onClick={() => setSelectedMessage(selectedMessage?.id === msg.id ? null : msg)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: selectedMessage?.id === msg.id ? '#4A90E2' : '#E3F2FD',
                          color: selectedMessage?.id === msg.id ? 'white' : '#1976D2'
                        }}
                      />
                    )}
                    {/* Thinking trail pills — one per reasoning step */}
                    {msg.reasoning && msg.reasoning.steps.map((step, stepIdx) => {
                      const s = step.step?.toLowerCase() || '';
                      const pillLabel = (() => {
                        if (s === 'gateway') return step.summary || 'Gateway';
                        if (s === 'research_context') return 'Research ctx';
                        if (s === 'research_plan') return 'Plan';
                        if (s === 'documents_found') return `${step.documents?.length || '?'} docs`;
                        if (s === 'blocks_retrieved') return step.summary?.match(/\d+/)?.[0] ? `${step.summary.match(/\d+/)![0]} blocks` : 'Blocks';
                        if (s === 'evidence_analysis') return 'Evidence';
                        if (s === 'themes_detected') return 'Themes';
                        if (s === 'parallel_analysis') return step.message || 'Parallel';
                        if (s === 'verification') return 'Verified';
                        return STEP_LABELS[step.step] || step.step;
                      })();
                      const style = STEP_STYLES[s] || { bg: '#f0f0f0', color: '#555', icon: null };
                      const msgPills = expandedPills.get(msg.id) || new Set<number>();
                      const isExpanded = msgPills.has(stepIdx);
                      return (
                        <Chip
                          key={stepIdx}
                          icon={style.icon as React.ReactElement || undefined}
                          label={pillLabel}
                          size="small"
                          onClick={() => {
                            setExpandedPills(prev => {
                              const next = new Map(prev);
                              const existing = next.get(msg.id) || new Set<number>();
                              const updated = new Set(existing);
                              if (updated.has(stepIdx)) updated.delete(stepIdx);
                              else updated.add(stepIdx);
                              next.set(msg.id, updated);
                              return next;
                            });
                          }}
                          sx={{
                            cursor: 'pointer',
                            bgcolor: isExpanded ? style.color : style.bg,
                            color: isExpanded ? 'white' : style.color,
                            fontSize: '0.7rem',
                            height: 24,
                            '& .MuiChip-icon': { color: isExpanded ? 'white' : style.color },
                            '&:hover': { bgcolor: isExpanded ? style.color : `${style.bg}dd`, opacity: 0.9 },
                          }}
                        />
                      );
                    })}
                    {/* Cost pill */}
                    {msg.reasoning?.cost && (
                      <Chip
                        icon={<CostIcon sx={{ fontSize: 14 }} />}
                        label={`$${msg.reasoning.cost.total_cost.toFixed(4)}`}
                        size="small"
                        onClick={() => setExpandedReasoning(prev => {
                          const next = new Set(prev);
                          if (next.has(msg.id)) next.delete(msg.id);
                          else next.add(msg.id);
                          return next;
                        })}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: expandedReasoning.has(msg.id) ? '#7B1FA2' : '#F3E5F5',
                          color: expandedReasoning.has(msg.id) ? 'white' : '#7B1FA2',
                          fontSize: '0.7rem',
                          height: 24,
                        }}
                      />
                    )}
                  </Box>
                ) : null}

                {/* Expanded pill details — inline below pills */}
                {msg.role === 'assistant' && msg.reasoning && (() => {
                  const msgPills = expandedPills.get(msg.id);
                  if (!msgPills || msgPills.size === 0) return null;
                  return (
                    <Box sx={{ maxWidth: '85%', mt: 0.5 }}>
                      {Array.from(msgPills).sort().map(stepIdx => {
                        const step = msg.reasoning!.steps[stepIdx];
                        if (!step) return null;
                        return (
                          <Box key={stepIdx} sx={{
                            mb: 0.5, p: 1, borderRadius: 1, fontSize: '0.8rem',
                            bgcolor: (STEP_STYLES[step.step] || {}).bg || '#f8f9fa',
                            border: `1px solid ${(STEP_STYLES[step.step] || {}).color || '#e0e0e0'}22`,
                          }}>
                            <Typography variant="caption" sx={{
                              fontWeight: 700,
                              color: (STEP_STYLES[step.step] || {}).color || '#555',
                              textTransform: 'uppercase', letterSpacing: 0.5
                            }}>
                              {STEP_LABELS[step.step] || step.step}
                            </Typography>
                            {step.documents ? (
                              <Box component="table" sx={{ width: '100%', mt: 0.5, borderCollapse: 'collapse', '& td, & th': { px: 1, py: 0.3, textAlign: 'left', fontSize: '0.75rem', borderBottom: '1px solid #eee' }, '& th': { color: '#777', fontWeight: 600 } }}>
                                <thead><tr><th>Document</th><th style={{ width: 60 }}>Score</th><th>Topics</th></tr></thead>
                                <tbody>
                                  {step.documents.map((doc: any, j: number) => (
                                    <tr key={j}>
                                      <td style={{ color: '#333' }}>{doc.title}</td>
                                      <td style={{ fontFamily: 'monospace', color: doc.confidence >= 0.7 ? '#2e7d32' : doc.confidence >= 0.4 ? '#e65100' : '#c62828' }}>
                                        {(doc.confidence * 100).toFixed(0)}%
                                      </td>
                                      <td>
                                        {doc.topics?.slice(0, 2).map((t: string, k: number) => (
                                          <Chip key={k} label={t} size="small" sx={{ mr: 0.25, mb: 0.25, height: 16, fontSize: '0.6rem', bgcolor: '#E8F5E9', color: '#2E7D32' }} />
                                        ))}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Box>
                            ) : step.reasoning ? (
                              /* Research plan expanded details */
                              <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ color: '#333', fontSize: '0.8rem', fontStyle: 'italic' }}>
                                  {step.reasoning}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                                  {step.tickers?.map((t: string, k: number) => (
                                    <Chip key={k} label={t} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 600 }} />
                                  ))}
                                  {step.sectors?.map((s: string, k: number) => (
                                    <Chip key={`s-${k}`} label={s} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#FFF3E0', color: '#E65100' }} />
                                  ))}
                                  {step.months_back && (
                                    <Chip label={`${step.months_back}mo lookback`} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#F3E5F5', color: '#6A1B9A' }} />
                                  )}
                                </Box>
                              </Box>
                            ) : step.summary ? (
                              <Typography variant="body2" sx={{ color: '#333', mt: 0.25, lineHeight: 1.4, fontSize: '0.8rem' }}>
                                {step.summary}
                              </Typography>
                            ) : step.message ? (
                              <Typography variant="body2" sx={{ color: '#333', mt: 0.25, lineHeight: 1.4, fontSize: '0.8rem' }}>
                                {step.message}
                              </Typography>
                            ) : null}
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })()}

                {/* Expanded cost breakdown */}
                {msg.role === 'assistant' && msg.reasoning?.cost && expandedReasoning.has(msg.id) && (
                  <Box sx={{ maxWidth: '80%' }}>
                    <ReasoningPanel reasoning={{ steps: [], cost: msg.reasoning.cost }} />
                  </Box>
                )}

                {/* Stats footer — compact line with time, docs, cost */}
                {msg.role === 'assistant' && msg.processingTimeMs && (
                  <Box sx={{
                    mt: 1, pt: 0.75, maxWidth: '85%',
                    display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap',
                    borderTop: '1px solid #f0f0f0',
                  }}>
                    <Typography variant="caption" sx={{ color: '#999', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {(msg.processingTimeMs / 1000).toFixed(1)}s
                    </Typography>
                    {msg.documentsUsed ? (
                      <Typography variant="caption" sx={{ color: '#999', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        {msg.documentsUsed} docs
                      </Typography>
                    ) : null}
                    {msg.reasoning?.cost && (
                      <>
                        <Typography variant="caption" sx={{ color: '#999', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                          {((msg.reasoning.cost.total_input_tokens + msg.reasoning.cost.total_output_tokens) / 1000).toFixed(1)}K tokens
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                          ${msg.reasoning.cost.total_cost.toFixed(4)}
                        </Typography>
                      </>
                    )}
                  </Box>
                )}

                {/* Follow-up suggestion chips */}
                {msg.role === 'assistant' && msg.suggestions && msg.suggestions.length > 0 && !loading && (
                  <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75, maxWidth: '85%' }}>
                    {msg.suggestions.map((suggestion, idx) => (
                      <Chip
                        key={idx}
                        label={suggestion}
                        size="small"
                        onClick={() => handleSend(suggestion)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: '#F5F5F5',
                          color: '#333',
                          border: '1px solid #E0E0E0',
                          fontSize: '0.8rem',
                          height: 'auto',
                          py: 0.5,
                          '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            display: 'block',
                            textAlign: 'left',
                          },
                          '&:hover': {
                            bgcolor: '#E3F2FD',
                            borderColor: '#90CAF9',
                          },
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
              );
            })
          )}

          {/* Loading/streaming indicator */}
          {loading && useSarah && (
            <StreamingProgress
              currentStage={pipelineStage}
              stageDetails={stageDetails}
              collapsed={progressCollapsed}
            />
          )}
          {loading && !useSarah && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <CircularProgress size={20} />
              <Typography color="text.secondary">
                {streamStatus || 'Searching documents...'}
              </Typography>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box sx={{ p: 2, borderTop: '1px solid #E0E0E0', bgcolor: 'white' }}>
          <Box sx={{ display: 'flex', gap: 1, maxWidth: 800, mx: 'auto' }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question..."
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  bgcolor: '#F5F5F5'
                },
                '& .MuiInputBase-input': {
                  direction: isHebrew(input) ? 'rtl' : 'ltr',
                  textAlign: isHebrew(input) ? 'right' : 'left'
                }
              }}
            />
            <IconButton
              onClick={() => handleSend()}
              disabled={loading || !input.trim()}
              sx={{ 
                bgcolor: '#4A90E2', 
                color: 'white',
                '&:hover': { bgcolor: '#357ABD' },
                '&.Mui-disabled': { bgcolor: '#ccc' }
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Sources Panel - Desktop */}
      {selectedMessage?.sources && selectedMessage.sources.length > 0 && (
        <Box sx={{
          width: 400,
          borderLeft: '1px solid #E0E0E0',
          bgcolor: 'white',
          overflow: 'hidden',
          display: { xs: 'none', md: 'flex' }
        }}>
          <GroupedSourcePanel
            sources={selectedMessage.sources}
            onClose={() => setSelectedMessage(null)}
          />
        </Box>
      )}

      {/* Sources Panel - Mobile Drawer */}
      <Drawer
        anchor="bottom"
        open={!!selectedMessage?.sources?.length && isMobile}
        onClose={() => setSelectedMessage(null)}
      >
        <Box sx={{ maxHeight: '70vh', overflow: 'hidden' }}>
          <GroupedSourcePanel
            sources={selectedMessage?.sources || []}
            onClose={() => setSelectedMessage(null)}
            isMobile
          />
        </Box>
      </Drawer>

      {/* Debug Panel - Bottom */}
      {debugVisible && (
        <DebugPanel
          logs={debugLogs}
          isOpen={debugOpen}
          onToggle={() => setDebugOpen(prev => !prev)}
          onClose={() => { setDebugVisible(false); setDebugOpen(false); }}
        />
      )}
    </Box>
  );
}
